import React, {
  useState,
  useEffect,
  useCallback,
  createContext,
  useContext,
  useMemo,
} from "react";
import ReactDOM from "react-dom";
import { useStore } from "reactflow";

interface FullscreenContextValue {
  isFullscreen: boolean;
  toggleFullscreen: () => void;
  exitFullscreen: () => void;
}

const FullscreenContext = createContext<FullscreenContextValue>({
  isFullscreen: false,
  toggleFullscreen: () => {},
  exitFullscreen: () => {},
});

export const useFullscreenContext = () => useContext(FullscreenContext);

interface FullscreenWrapperProps {
  id: string;
  isExpanded: boolean;
  children: React.ReactNode;
  borderColor?: string;
  shadowColor?: string;
  executionStatus?: string | null;
  onContextMenu?: (e: React.MouseEvent) => void;
  expandedClassName?: string;
  collapsedClassName?: string;
  collapsedStyle?: React.CSSProperties;
}

export const FullscreenWrapper: React.FC<FullscreenWrapperProps> = ({
  id,
  isExpanded,
  children,
  borderColor,
  shadowColor,
  executionStatus,
  onContextMenu,
  expandedClassName,
  collapsedClassName,
  collapsedStyle,
}) => {
  const zoom = useStore((s) => s.transform[2]);
  const isSelected = useStore(
    (s) => s.nodeInternals.get(id)?.selected ?? false,
  );
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showLoadingBorder, setShowLoadingBorder] = useState(false);

  const toggleFullscreen = useCallback(() => setIsFullscreen((p) => !p), []);
  const exitFullscreen = useCallback(() => setIsFullscreen(false), []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "F") {
        if (isExpanded && isSelected) {
          e.preventDefault();
          e.stopPropagation();
          setIsFullscreen((p) => !p);
        }
      }
      if (e.key === "Escape" && isFullscreen) {
        const hasOpenDialog = document.querySelector(
          '[role="dialog"][data-state="open"]',
        );
        if (hasOpenDialog) return;
        e.stopPropagation();
        setIsFullscreen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown, { capture: true });
    return () =>
      window.removeEventListener("keydown", handleKeyDown, { capture: true });
  }, [isExpanded, isFullscreen, isSelected]);

  useEffect(() => {
    if (!isExpanded) setIsFullscreen(false);
  }, [isExpanded]);

  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isFullscreen]);

  useEffect(() => {
    if (isFullscreen) {
      document.body.setAttribute("data-node-fullscreen", "true");
      const styleEl = document.createElement("style");
      styleEl.id = "fullscreen-zindex-fix";
      styleEl.textContent = `
        body[data-node-fullscreen="true"] > div[data-radix-popper-content-wrapper],
        body[data-node-fullscreen="true"] > div[role="dialog"],
        body[data-node-fullscreen="true"] > div[data-state="open"] {
          z-index: 100000 !important;
        }
      `;
      document.head.appendChild(styleEl);
    } else {
      document.body.removeAttribute("data-node-fullscreen");
      document.getElementById("fullscreen-zindex-fix")?.remove();
    }
    return () => {
      document.body.removeAttribute("data-node-fullscreen");
      document.getElementById("fullscreen-zindex-fix")?.remove();
    };
  }, [isFullscreen]);

  const isZoomedOut = zoom < 0.5;
  const zoomPercentage = Math.round(zoom * 100);
  const loadingBorderColor = borderColor || "var(--primary)";
  const isExecutionLoading =
    executionStatus === "running" || executionStatus === "pending";

  useEffect(() => {
    if (isExecutionLoading && !isFullscreen && !isExpanded) {
      setShowLoadingBorder(true);
      return;
    }

    if (isFullscreen || isExpanded) {
      setShowLoadingBorder(false);
      return;
    }

    const timer = setTimeout(() => setShowLoadingBorder(false), 350);
    return () => clearTimeout(timer);
  }, [isExecutionLoading, isFullscreen, isExpanded]);

  const contextValue = useMemo(
    () => ({ isFullscreen, toggleFullscreen, exitFullscreen }),
    [isFullscreen, toggleFullscreen, exitFullscreen],
  );

  return (
    <FullscreenContext.Provider value={contextValue}>
      {isFullscreen &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 animate-in fade-in-0 duration-200"
            style={{ backgroundColor: "#000000b4", zIndex: 49 }}
            onClick={exitFullscreen}
          />,
          document.body,
        )}

      {isFullscreen ? (
        ReactDOM.createPortal(
          // KEY: flex flex-col + overflow-hidden makes this the top of the
          // flex chain. inset-6 is the single height authority in fullscreen.
          // Every child that needs to stretch uses flex-1 min-h-0.
          <div
            className="fixed inset-6 bg-background border shadow-lg rounded-xl overflow-hidden p-2 animate-in fade-in-0 zoom-in-95 duration-200 flex flex-col"
            style={{
              borderColor,
              boxShadow: "0 0 50px rgba(0,0,0,0.5)",
              zIndex: 50,
            }}
            onContextMenu={onContextMenu}
          >
            {children}
          </div>,
          document.body,
        )
      ) : (
        <>
          <style>
            {`
              .rf-node-shell .react-flow__handle-left:not([style*="top"]),
              .rf-node-shell .react-flow__handle-right:not([style*="top"]) {
                top: 50% !important;
                transform: translateY(-50%) !important;
                z-index: 20 !important;
              }

              .rf-node-shell .react-flow__handle-left:not([style*="left"]) {
                left: -8px !important;
              }

              .rf-node-shell .react-flow__handle-right:not([style*="right"]) {
                right: -8px !important;
              }
            `}
          </style>
          {showLoadingBorder && (
            <style>
              {`
                @keyframes spin-border {
                  to {
                    transform: rotate(360deg);
                  }
                }

                .rf-loading-border {
                  position: absolute;
                  inset: -1px;
                  border-radius: inherit;
                  overflow: hidden;
                  pointer-events: none;
                  z-index: 0;
                }

                .rf-loading-border::before {
                  content: '';
                  position: absolute;
                  inset: -50%;
                  background: conic-gradient(
                    from 0deg,
                    transparent 0deg,
                    transparent 300deg,
                    var(--rf-loading-color, var(--primary)) 340deg,
                    var(--rf-loading-color, var(--primary)) 360deg
                  );
                  animation: spin-border 2s linear infinite;
                }

                .rf-loading-border::after {
                  content: '';
                  position: absolute;
                  inset: 2px;
                  background: var(--background);
                  border-radius: inherit;
                }
              `}
            </style>
          )}
          <div
            className={`rf-node-shell relative bg-background border shadow-lg rounded-2xl p-2 transition-all ${
              isExpanded
                ? (expandedClassName ?? "w-[90vw] !max-w-[1000px]")
                : (collapsedClassName ??
                  "w-40 h-28 p-6 max-w-80 flex items-center justify-center")
            }`}
            style={{
              border: showLoadingBorder
                ? "1px solid var(--light-gray)"
                : borderColor
                  ? `1px solid ${borderColor}`
                  : undefined,
              boxShadow: showLoadingBorder
                ? "none"
                : `0 4px 12px ${shadowColor}`,
              ...(!isExpanded && collapsedStyle ? collapsedStyle : {}),
              ...(showLoadingBorder
                ? ({
                    "--rf-loading-color": loadingBorderColor,
                  } as React.CSSProperties)
                : {}),
            }}
            onContextMenu={onContextMenu}
            title={
              isZoomedOut
                ? `Zoom: ${zoomPercentage}% — Ctrl+Shift+F to fullscreen`
                : undefined
            }
          >
            {showLoadingBorder && <div className="rf-loading-border" />}
            {children}
          </div>
        </>
      )}
    </FullscreenContext.Provider>
  );
};

export default FullscreenWrapper;
